import { isScene, type ChoiceResult, type GameContext, type Scene } from '../content/types';
import type { Outcome } from '../state/Store';
import { buzzTier } from '../state/rules';
import { setRich } from '../icons/dom';
import { richLength, richSlice } from '../icons/rich';
import { clear, h, layer } from './dom';

export interface SceneHost {
  ctx: GameContext;
  portrait(id: string): string | undefined;
  blip(pitch: number): void;
  onOpen(): void;
  onClose(): void;
}

/**
 * Dialogue / event modal. Shows a scene's text with a typewriter effect, numbered
 * choices, then a result page summarizing what changed.
 */
export class SceneModal {
  readonly el: HTMLDivElement;
  private readonly panel: HTMLDivElement;
  private open = false;
  private token = 0;
  private typing: number | null = null;
  private keyHandler: ((e: KeyboardEvent) => boolean) | null = null;
  private onCloseOnce: (() => void) | null = null;

  constructor(
    parent: HTMLElement,
    private readonly host: SceneHost,
  ) {
    this.el = layer();
    this.panel = h('div', { class: 'modal cardboard', attrs: { role: 'dialog', 'aria-modal': 'true' } });
    this.el.append(this.panel);
    parent.append(this.el);
    window.addEventListener('keydown', this.onKey, true);
  }

  get isOpen(): boolean {
    return this.open;
  }

  show(scene: Scene, onClose?: () => void): void {
    if (!this.open) {
      this.open = true;
      this.el.hidden = false;
      this.host.onOpen();
    }
    if (onClose) this.onCloseOnce = onClose;
    this.token++;
    this.render(scene);
  }

  close(): void {
    if (!this.open) return;
    this.stopTyping();
    this.open = false;
    this.el.hidden = true;
    this.keyHandler = null;
    const cb = this.onCloseOnce;
    this.onCloseOnce = null;
    this.host.onClose();
    cb?.();
  }

  private onKey = (e: KeyboardEvent): void => {
    if (!this.open || !this.keyHandler) return;
    if (this.keyHandler(e)) {
      e.preventDefault();
      e.stopImmediatePropagation();
    }
  };

  private stopTyping(): void {
    if (this.typing !== null) window.clearInterval(this.typing);
    this.typing = null;
  }

  private typewrite(el: HTMLElement, text: string, voice?: number): void {
    this.stopTyping();
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setRich(el, text);
      return;
    }
    // Icons type in as one character each.
    const length = richLength(text);
    let i = 0;
    setRich(el, '');
    const finish = (): void => {
      this.stopTyping();
      setRich(el, text);
    };
    el.onclick = finish;
    this.typing = window.setInterval(() => {
      i += 2;
      const shown = richSlice(text, i);
      setRich(el, shown);
      if (voice && i % 6 === 0 && /\w/.test(shown.slice(-1))) this.host.blip(voice);
      if (i >= length) finish();
    }, 16);
  }

  private head(scene: Scene): HTMLElement {
    const portrait = scene.speaker ? this.host.portrait(scene.speaker.id) : undefined;
    const pic = portrait
      ? h('img', { class: 'portrait', attrs: { src: portrait, alt: scene.speaker?.name ?? '' } })
      : h('div', { class: 'art', text: scene.art ?? ':speech:' });
    return h(
      'div',
      { class: 'modal-head' },
      pic,
      h(
        'div',
        {},
        h('div', { class: 'modal-title', text: scene.title }),
        scene.speaker ? h('div', { class: 'modal-speaker', text: scene.speaker.name }) : null,
      ),
    );
  }

  private render(scene: Scene): void {
    clear(this.panel);
    const textEl = h('div', { class: 'modal-text' });
    const choicesEl = h('div', { class: 'choices' });
    const hammered = buzzTier(this.host.ctx.store.state.stats.buzz) === 'hammered';
    const buttons: Array<{ btn: HTMLButtonElement; run: () => void }> = [];
    scene.choices.forEach((choice, idx) => {
      const ok = choice.requires ? choice.requires(this.host.ctx) : true;
      const label = hammered && Math.random() < 0.5 ? `${choice.label} *hic*` : choice.label;
      const run = (): void => this.choose(choice.run);
      const btn = h(
        'button',
        { class: 'tape', disabled: ok !== true, on: { click: run } },
        h('span', { class: 'key', text: String(idx + 1) }),
        label,
        choice.hint ? h('span', { class: 'hint', text: choice.hint }) : null,
        ok !== true ? h('span', { class: 'why', text: ok }) : null,
      );
      choicesEl.append(btn);
      if (ok === true) buttons[idx] = { btn, run };
    });
    const canLeave = scene.leave !== false;
    if (canLeave) {
      const leave = h(
        'button',
        { class: 'tape', on: { click: () => this.close() } },
        h('span', { class: 'key', text: 'Esc' }),
        typeof scene.leave === 'string' ? scene.leave : scene.choices.length ? 'Catch ya later' : 'Aight',
      );
      choicesEl.append(leave);
    }
    this.panel.append(this.head(scene), textEl, choicesEl);
    this.typewrite(textEl, scene.text, scene.speaker?.voice);
    (buttons.find(Boolean)?.btn ?? (choicesEl.lastElementChild as HTMLElement | null))?.focus({
      preventScroll: true,
    });

    this.keyHandler = (e) => {
      const n = Number.parseInt(e.key, 10);
      if (n >= 1 && n <= 9) {
        buttons[n - 1]?.run();
        return true;
      }
      if (e.key === 'Escape' && canLeave) {
        this.close();
        return true;
      }
      if (
        (e.key === 'Enter' || e.code === 'KeyE' || e.code === 'Space') &&
        scene.choices.length === 0 &&
        canLeave
      ) {
        this.close();
        return true;
      }
      return ['KeyE', 'Space', 'KeyQ', 'KeyF', 'KeyR', 'KeyT', 'KeyG', 'KeyP'].includes(e.code);
    };
  }

  private choose(run: (ctx: GameContext) => ChoiceResult): void {
    const before = this.token;
    const result = run(this.host.ctx);
    if (this.token !== before || !this.open) return; // the choice opened something else
    if (result === undefined) {
      this.close();
      return;
    }
    if (isScene(result)) {
      this.show(result);
      return;
    }
    const outcome: Outcome = typeof result === 'string' ? { text: result } : result;
    const chips = this.host.ctx.store.apply(outcome);
    this.showResult(outcome.text ?? 'Done.', chips);
  }

  private showResult(text: string, chips: string[]): void {
    clear(this.panel);
    const textEl = h('div', { class: 'modal-text' });
    const ok = h(
      'button',
      { class: 'tape primary', on: { click: () => this.close() } },
      h('span', { class: 'key', text: 'E' }),
      'Aight',
    );
    this.panel.append(textEl);
    if (chips.length) {
      this.panel.append(
        h(
          'div',
          { class: 'chips' },
          ...chips.map((c) => h('span', { class: `chip ${c.startsWith('-') ? 'neg' : ''}`, text: c })),
        ),
      );
    }
    this.panel.append(h('div', { class: 'choices' }, ok));
    this.typewrite(textEl, text);
    ok.focus({ preventScroll: true });
    this.keyHandler = (e) => {
      if (e.key === 'Enter' || e.key === 'Escape' || e.code === 'KeyE' || e.code === 'Space') {
        this.close();
        return true;
      }
      return false;
    };
  }
}
